/* <audio-player> — play/pause, animated waveform fill, countdown timer, transcript toggle.
   Self-contained so it works both on the web PDP and inside the Stikky app WebView. */
(function () {
  if (window.customElements && window.customElements.get('audio-player')) return;

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  class AudioPlayer extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.audio = this.querySelector('audio');
      this.toggleBtn = this.querySelector('[data-audio-toggle]');
      this.timeEl = this.querySelector('[data-audio-time]');
      this.transcriptBtn = this.querySelector('[data-audio-transcript-toggle]');
      if (!this.audio) return;

      this.toggleBtn && this.toggleBtn.addEventListener('click', this.toggle.bind(this));
      this.transcriptBtn && this.transcriptBtn.addEventListener('click', this.toggleTranscript.bind(this));

      this.audio.addEventListener('loadedmetadata', this.showDuration.bind(this));
      this.audio.addEventListener('timeupdate', this.updateProgress.bind(this));
      this.audio.addEventListener('play', this.onPlay.bind(this));
      this.audio.addEventListener('pause', this.onPause.bind(this));
      this.audio.addEventListener('ended', this.reset.bind(this));
      this.audio.addEventListener('error', this.onError.bind(this));

      // Diagnostics: trace the playback lifecycle so we can see why audio that
      // "plays" (icon flips) produces no sound — buffering vs. silent output.
      var self = this;
      ['playing', 'waiting', 'stalled', 'suspend', 'canplay', 'canplaythrough', 'loadeddata', 'volumechange'].forEach(function (evt) {
        self.audio.addEventListener(evt, function () { self.logState(evt); });
      });

      // Metadata may already be available if the script loaded after parsing.
      if (this.audio.readyState >= 1) this.showDuration();
    }

    logState(event) {
      if (!window.console) return;
      var a = this.audio;
      console.log('[audio-player]', event, {
        currentTime: a.currentTime,
        duration: a.duration,
        paused: a.paused,
        muted: a.muted,
        volume: a.volume,
        readyState: a.readyState, // 0 NOTHING .. 4 ENOUGH_DATA
        networkState: a.networkState, // 0 EMPTY,1 IDLE,2 LOADING,3 NO_SOURCE
        currentSrc: a.currentSrc,
      });
    }

    toggle() {
      if (!this.audio.paused) {
        this.audio.pause();
        return;
      }
      // play() returns a promise that iOS WebKit rejects when it blocks the
      // request (e.g. unsupported codec, missing user gesture). Surface it
      // instead of failing silently, and retry once after a fresh load().
      var self = this;
      var attempt = this.audio.play();
      if (attempt && attempt.then) {
        attempt.then(
          function () {
            // play() resolved — the element accepted playback. If there is still
            // no sound, the state below (muted/volume) or the OS silent switch
            // is the culprit, not the web code.
            if (window.console) console.log('[audio-player] play() resolved');
            self.logState('play-resolved');
          },
          function (err) {
            if (window.console) console.warn('[audio-player] play() rejected:', err && err.name, err && err.message);
            if (!self._retried) {
              self._retried = true;
              try {
                self.audio.load();
                var retry = self.audio.play();
                if (retry && retry.catch) retry.catch(function (e) {
                  if (window.console) console.warn('[audio-player] retry play() rejected:', e && e.name, e && e.message);
                });
              } catch (e) {
                if (window.console) console.warn('[audio-player] retry threw:', e);
              }
            }
          }
        );
      }
    }

    onError() {
      // MediaError codes: 1 ABORTED, 2 NETWORK, 3 DECODE, 4 SRC_NOT_SUPPORTED.
      // Code 4 on iOS usually means the codec/container is unsupported, or the
      // host doesn't serve HTTPS + HTTP byte-range (206) requests.
      var err = this.audio.error;
      if (window.console) console.warn('[audio-player] media error', err && err.code, this.audio.currentSrc);
    }

    onPlay() {
      this.setAttribute('playing', '');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-label', this.toggleBtn.getAttribute('data-label-pause') || '');
      }
    }

    onPause() {
      this.removeAttribute('playing');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-label', this.toggleBtn.getAttribute('data-label-play') || '');
      }
    }

    showDuration() {
      this._duration = this.audio.duration;
      // Show total length until playback starts (then the timer counts down).
      if (this.timeEl && !this.hasAttribute('playing')) {
        this.timeEl.textContent = formatTime(this._duration);
      }
    }

    updateProgress() {
      var duration = this.audio.duration || this._duration || 0;
      var current = this.audio.currentTime || 0;
      var percent = duration ? (current / duration) * 100 : 0;
      // Set on the host so both the waveform fill and the playhead read it via inheritance.
      this.style.setProperty('--progress', percent + '%');
      if (this.timeEl) this.timeEl.textContent = formatTime(duration - current);
    }

    reset() {
      this.removeAttribute('playing');
      this.audio.currentTime = 0;
      this.style.setProperty('--progress', '0%');
      if (this.timeEl) this.timeEl.textContent = formatTime(this._duration || this.audio.duration);
    }

    toggleTranscript() {
      var open = this.toggleAttribute('transcript-open');
      if (this.transcriptBtn) this.transcriptBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  window.customElements.define('audio-player', AudioPlayer);
})();
